import { Container, Typography, Box, Paper, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const TermsAndConditionsPage = () => {
  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      <Paper elevation={3} sx={{ p: { xs: 3, md: 5 } }}>
        <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
          Terms and Conditions
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Last Updated: February 26, 2026
        </Typography>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            1. Acceptance of Terms
          </Typography>
          <Typography variant="body1" paragraph>
            By accessing or using ServeSync at servesync.me (the "Service"), you agree to be bound
            by these Terms and Conditions ("Terms"). If you do not agree to these Terms, do not use
            the Service. ServeSync reserves the right to modify these Terms at any time, and your
            continued use of the Service constitutes acceptance of any changes.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            2. Description of Service
          </Typography>
          <Typography variant="body1" paragraph>
            ServeSync is a volunteer coordination platform that connects organizations with volunteers.
            The Service allows organizations to create and manage volunteer events, and allows
            volunteers to discover, register for, and receive reminders about events. The Service
            includes web-based tools, email notifications, and optional SMS text message reminders.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            3. Account Registration
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            <ul>
              <li>You must provide accurate, current, and complete information when creating an account.</li>
              <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
              <li>You are responsible for all activity that occurs under your account.</li>
              <li>You must be at least 13 years of age to use the Service.</li>
              <li>You agree to notify us immediately of any unauthorized use of your account.</li>
            </ul>
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            4. User Conduct
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            You agree not to:
            <ul>
              <li>Provide false or misleading information in your profile or event registrations.</li>
              <li>Use the Service for any unlawful purpose or in violation of any applicable laws.</li>
              <li>Interfere with or disrupt the Service or its infrastructure.</li>
              <li>Attempt to gain unauthorized access to any part of the Service.</li>
              <li>Harass, abuse, or harm other users of the Service.</li>
              <li>Use the Service to send spam or unsolicited communications.</li>
              <li>Scrape, crawl, or otherwise extract data from the Service without permission.</li>
            </ul>
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            5. SMS Text Message Terms
          </Typography>
          <Typography variant="body1" paragraph>
            By enabling SMS reminders on your Profile page, you expressly consent to receive
            automated text messages from ServeSync at the phone number you provide. You understand
            and agree to the following:
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            <ul>
              <li><strong>Opt-In:</strong> SMS reminders are optional. You must explicitly enable the "SMS Reminders" toggle and provide your phone number to receive messages.</li>
              <li><strong>Message Types:</strong> You will receive event reminder messages (sent 24 hours before events you are registered for) and a one-time opt-in confirmation message.</li>
              <li><strong>Message Frequency:</strong> Message frequency varies based on the number of events you register for. You will receive at most one reminder per registered event.</li>
              <li><strong>Costs:</strong> Message and data rates may apply. ServeSync does not charge for sending SMS messages, but your mobile carrier may charge standard messaging fees.</li>
              <li><strong>Opt-Out:</strong> You may opt out at any time by replying STOP to any message or by disabling the "SMS Reminders" toggle in your Profile settings. Reply HELP for assistance.</li>
              <li><strong>No Purchase Required:</strong> Consent to receive SMS messages is not a condition of purchasing any goods or services.</li>
              <li><strong>Carrier Disclaimer:</strong> Carriers are not liable for delayed or undelivered messages.</li>
            </ul>
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            6. Organization Responsibilities
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            Organizations using ServeSync to manage volunteer events agree to:
            <ul>
              <li>Provide accurate event information including dates, times, locations, and descriptions.</li>
              <li>Maintain appropriate supervision and safety measures at events.</li>
              <li>Comply with all applicable laws and regulations related to volunteer activities.</li>
              <li>Use volunteer data only for event coordination purposes.</li>
            </ul>
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            7. Intellectual Property
          </Typography>
          <Typography variant="body1" paragraph>
            The Service, including its design, features, content, and underlying technology, is owned
            by ServeSync and protected by applicable intellectual property laws. You may not copy,
            modify, distribute, or create derivative works based on the Service without our express
            written permission.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            8. Privacy
          </Typography>
          <Typography variant="body1" paragraph>
            Your use of the Service is also governed by our{' '}
            <Link component={RouterLink} to="/privacy">Privacy Policy</Link>, which describes how
            we collect, use, and protect your personal information. By using the Service, you consent
            to the practices described in the Privacy Policy.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            9. Disclaimer of Warranties
          </Typography>
          <Typography variant="body1" paragraph>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
            EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
            MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. SERVESYNC
            DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            10. Limitation of Liability
          </Typography>
          <Typography variant="body1" paragraph>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, SERVESYNC SHALL NOT BE LIABLE FOR ANY INDIRECT,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO
            LOSS OF DATA, LOSS OF PROFITS, OR PERSONAL INJURY ARISING OUT OF YOUR USE OF THE
            SERVICE. SERVESYNC'S TOTAL LIABILITY FOR ANY CLAIM ARISING FROM THE SERVICE SHALL NOT
            EXCEED THE AMOUNT YOU HAVE PAID TO SERVESYNC IN THE TWELVE MONTHS PRECEDING THE CLAIM.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            11. Indemnification
          </Typography>
          <Typography variant="body1" paragraph>
            You agree to indemnify, defend, and hold harmless ServeSync from any claims, damages,
            losses, liabilities, and expenses (including reasonable attorneys' fees) arising out of
            your use of the Service, your violation of these Terms, or your violation of any rights
            of a third party.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            12. Termination
          </Typography>
          <Typography variant="body1" paragraph>
            We may suspend or terminate your access to the Service at any time, with or without cause,
            and with or without notice. You may stop using the Service at any time. Upon termination,
            your right to use the Service ceases immediately. To request deletion of your account and
            data, please contact us through our{' '}
            <Link component={RouterLink} to="/support">support page</Link>.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            13. Governing Law
          </Typography>
          <Typography variant="body1" paragraph>
            These Terms shall be governed by and construed in accordance with the laws of the United
            States and the state in which ServeSync operates, without regard to conflict of law
            principles.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            14. Changes to These Terms
          </Typography>
          <Typography variant="body1" paragraph>
            We reserve the right to update these Terms at any time. Changes will be posted on this
            page with an updated "Last Updated" date. Your continued use of the Service after any
            changes constitutes acceptance of the revised Terms.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            15. Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            If you have questions about these Terms, please contact us through our{' '}
            <Link component={RouterLink} to="/support">support page</Link>.
          </Typography>
          <Typography variant="body1">
            ServeSync - servesync.me
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default TermsAndConditionsPage;
